import { useMemo } from "react";
import { OpportunityStatistics as OpportunityStatisticsType } from "@forgepoint/shared";
import { Decimal } from "decimal.js";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface OpportunityStatisticsProps {
  statistics: OpportunityStatisticsType | null;
  isLoading?: boolean;
  hideWinRate?: boolean;
  showDashes?: boolean;
}

/**
 * Displays key metrics for opportunities in a compact, inline format:
 * - Total amount in progress (sum of INTAKE, QUOTE_IN_PROGRESS, and SENT)
 * - Weighted amount in progress (adjusted by probability)
 * - Win rate percentage (ACCEPTED / (ACCEPTED + LOST)), unless hideWinRate is set
 * Shows skeleton state while statistics are loading.
 */
export function OpportunityStatistics({
  statistics,
  isLoading = false,
  hideWinRate = false,
  showDashes = false,
}: OpportunityStatisticsProps) {
  const computed = useMemo(() => {
    if (!statistics) {
      return null;
    }

    const amountInProgress = new Decimal(statistics.amountInProgress);
    const weightedAmountInProgress = new Decimal(
      statistics.weightedAmountInProgress,
    );
    const winRate =
      statistics.closedCount > 0
        ? (statistics.acceptedCount / statistics.closedCount) * 100
        : null;

    return { amountInProgress, weightedAmountInProgress, winRate };
  }, [statistics]);

  const formatCompactCurrency = (amount: Decimal): string => {
    const value = amount.toNumber();
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
    }
    if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number | null): string => {
    if (value === null) {
      return "N/A";
    }
    return `${value.toFixed(0)}%`;
  };

  if (showDashes) {
    return (
      <div className="flex items-center gap-3 px-4 text-sm">
        <div className="flex items-baseline gap-1.5">
          <span className="text-xl font-semibold tabular-nums">—</span>
          <span className="text-muted-foreground">in progress</span>
        </div>
        <span className="text-border">·</span>
        <div className="flex items-baseline gap-1.5">
          <span className="text-xl font-semibold tabular-nums">—</span>
          <span className="text-muted-foreground">weighted in progress</span>
        </div>
        {!hideWinRate && (
          <>
            <span className="text-border">·</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl font-semibold tabular-nums">—</span>
              <span className="text-muted-foreground">win rate</span>
            </div>
          </>
        )}
      </div>
    );
  }

  if (isLoading || !computed) {
    return (
      <div className="flex items-center gap-2 px-4 text-sm">
        <Skeleton className="h-6 w-20" />
        <span className="text-muted-foreground">•</span>
        <Skeleton className="h-6 w-20" />
        {!hideWinRate && (
          <>
            <span className="text-muted-foreground">•</span>
            <Skeleton className="h-6 w-16" />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 text-sm">
      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums">
          {formatCompactCurrency(computed.amountInProgress)}
        </span>
        <span className="text-muted-foreground">in progress</span>
      </div>

      <span className="text-border">·</span>

      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums">
          {formatCompactCurrency(computed.weightedAmountInProgress)}
        </span>
        <span className="text-muted-foreground">weighted in progress</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[220px]">
              <p>
                In-progress deal values adjusted by each opportunity's
                probability of closing
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {!hideWinRate && (
        <>
          <span className="text-border">·</span>

          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-semibold tabular-nums">
              {formatPercentage(computed.winRate)}
            </span>
            <span className="text-muted-foreground">win rate</span>
          </div>
        </>
      )}
    </div>
  );
}
